import { Section, Card, ButtonLink, Eyebrow } from "../components/ui";
import { Reveal } from "../components/Reveal";
import { site } from "../data/site";

/**
 * The closing ask.
 *
 * Deliberately not a form. The form lives on /contact; putting one at the
 * end of every page turns the last screen into a chore and buries the
 * single most useful line here, which is that a human reads the reply.
 */
export function CTASection({
  title = (
    <>
      Have a problem <span className="text-gradient">worth solving?</span>
    </>
  ),
  lede = "Tell us what you are trying to build, automate or improve. We will work out the technology with you — and say so if the honest answer is that you do not need us.",
  primary = { href: "/contact", label: "Start a conversation" },
  secondary = { href: "/products", label: "Explore our products" },
}: {
  title?: React.ReactNode;
  lede?: string;
  primary?: { href: string; label: string };
  secondary?: { href: string; label: string };
} = {}) {
  return (
    <Section id="cta">
      <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[380px] w-[760px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-violet/15 blur-[120px]" />
      <div className="container">
        <Reveal>
          <Card className="relative overflow-hidden px-6 py-12 text-center sm:px-10 sm:py-16">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" />

            <div className="mx-auto max-w-2xl">
              <Eyebrow>Start here</Eyebrow>
              <h2 className="mt-5 font-display text-[2rem] font-bold leading-[1.08] tracking-tight sm:text-[2.75rem]">
                {title}
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-white/65 sm:text-lg">
                {lede}
              </p>

              <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
                <ButtonLink href={primary.href}>{primary.label}</ButtonLink>
                <ButtonLink href={secondary.href} variant="ghost" icon={false}>
                  {secondary.label}
                </ButtonLink>
              </div>

              <p className="mt-6 text-xs text-white/40">
                Or write to{" "}
                <a
                  href={`mailto:${site.email}`}
                  className="text-white/65 underline-offset-2 transition-colors hover:text-white hover:underline"
                >
                  {site.email}
                </a>
                . A person reads it, and replies within one working day.
              </p>
            </div>
          </Card>
        </Reveal>
      </div>
    </Section>
  );
}
